<template>
  <div class="intro-page">
    <AnimatedBackground />

    <section class="hero-section" aria-label="Présentation de Vitrail-A">
      <div class="hero-content">
        <div class="logo-container">
          <img src="@/assets/logo.png" alt="Vitrail-A" class="hero-logo" />
        </div>

        <p class="hero-kicker">Carnet de santé numérique vétérinaire</p>
        <h1 class="hero-title">Le dossier santé qui suit vos animaux partout</h1>
        <p class="hero-subtitle">
          Vitrail-A centralise les informations médicales, l'identification, les cartes QR et les ventes d'animaux sur une plateforme sécurisée pensée pour les propriétaires, éleveurs et vétérinaires.
        </p>

        <div class="hero-pillars" aria-label="Avantages principaux">
          <div v-for="pillar in heroPillars" :key="pillar.title" class="hero-pillar">
            <i :class="pillar.icon"></i>
            <span>{{ pillar.title }}</span>
          </div>
        </div>

        <div class="hero-badges">
          <span v-for="badge in heroBadges" :key="badge.label" class="badge">
            <i :class="badge.icon"></i>
            {{ badge.label }}
          </span>
        </div>

        <div class="hero-cta">
          <GlassButton variant="success" size="lg" icon="fas fa-user-plus" @click="$router.push('/inscription')">
            Créer un compte
          </GlassButton>
          <GlassButton variant="outline" size="lg" icon="fas fa-chevron-down" @click="scrollToSection('how-it-works')">
            Voir le fonctionnement
          </GlassButton>
        </div>
      </div>
    </section>

    <section class="stats-section" aria-label="Chiffres clés">
      <div class="stats-container">
        <div v-for="stat in stats" :key="stat.label" class="stat-item">
          <i :class="stat.icon"></i>
          <div>
            <strong>{{ stat.value }}</strong>
            <span>{{ stat.label }}</span>
          </div>
        </div>
      </div>
    </section>

    <section id="how-it-works" class="process-section" aria-label="Comment ça marche">
      <div class="section-inner">
        <div class="section-heading centered">
          <span class="section-eyebrow">Simple au quotidien</span>
          <h2>Un parcours clair, du premier animal à la carte QR</h2>
          <p>Vous ajoutez vos animaux une fois, puis Vitrail-A garde le dossier disponible pour les moments qui comptent : consultation, vente, transfert ou contrôle.</p>
        </div>

        <div class="process-steps">
          <article v-for="step in processSteps" :key="step.title" class="process-step">
            <span class="step-number">{{ step.number }}</span>
            <i :class="step.icon"></i>
            <h3>{{ step.title }}</h3>
            <p>{{ step.text }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="card-spotlight" aria-label="Carte physique Vitrail-A">
      <div class="section-inner card-spotlight-inner">
        <div class="spotlight-copy">
          <span class="section-eyebrow">Produit phare</span>
          <h2>Une carte physique avec QR code pour accéder au dossier en quelques secondes</h2>
          <p>
            La carte Vitrail-A relie l'identification de l'animal à son dossier numérique. Elle rassure lors d'une vente, simplifie les consultations et rend les informations utiles disponibles au bon moment.
          </p>

          <ul class="spotlight-list">
            <li v-for="feature in cardFeatures" :key="feature">
              <i class="fas fa-check"></i>
              <span>{{ feature }}</span>
            </li>
          </ul>

          <GlassButton variant="success" icon="fas fa-id-card" @click="$router.push('/inscription')">
            Commander une carte
          </GlassButton>
        </div>

        <div class="card-preview" aria-hidden="true">
          <div class="health-card-mock">
            <div class="card-topline">
              <img src="@/assets/logo.png" alt="" />
              <span>VITRAIL-A</span>
            </div>
            <div class="card-animal">
              <strong>Olympe du Parc</strong>
              <span>Carte santé animale</span>
            </div>
            <div class="card-meta">
              <span>UELN 250001</span>
              <span>QR dossier</span>
            </div>
            <div class="qr-box">
              <i class="fas fa-qrcode"></i>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div id="benefits">
      <section
        v-for="audience in audienceSections"
        :key="audience.title"
        class="benefit-section"
        :class="{ 'benefit-alt': audience.alt }"
        :aria-label="audience.ariaLabel"
      >
        <div class="benefit-inner">
          <div class="section-header">
            <GlassIcon :name="audience.glassIcon" size="lg" :variant="audience.iconVariant" :animated="true" />
            <div>
              <span class="section-eyebrow">{{ audience.eyebrow }}</span>
              <h2>{{ audience.title }}</h2>
              <p class="section-tagline">{{ audience.tagline }}</p>
            </div>
          </div>

          <div class="benefits-grid compact-grid">
            <article v-for="benefit in audience.benefits" :key="benefit.title" class="benefit-card">
              <div class="benefit-icon" :class="audience.iconClass"><i :class="benefit.icon"></i></div>
              <h3>{{ benefit.title }}</h3>
              <p>{{ benefit.text }}</p>
            </article>
          </div>

          <div v-if="audience.ctaLabel" class="section-cta">
            <GlassButton :variant="audience.buttonVariant" icon-right="fas fa-arrow-right" @click="$router.push(audience.ctaRoute)">
              {{ audience.ctaLabel }}
            </GlassButton>
          </div>
        </div>
      </section>
    </div>

    <section class="testimonials-section" aria-label="Avis clients">
      <div class="section-inner">
        <div class="section-heading centered">
          <span class="section-eyebrow">Retours clients</span>
          <h2>Une page prête à accueillir vos avis vérifiés</h2>
          <p>Ces emplacements peuvent recevoir vos témoignages propriétaires, éleveurs et vétérinaires dès qu'ils sont validés.</p>
        </div>

        <div class="testimonials-grid">
          <article v-for="testimonial in testimonials" :key="testimonial.role" class="testimonial-card">
            <div class="rating" aria-label="Note cinq étoiles">
              <i v-for="star in 5" :key="star" class="fas fa-star"></i>
            </div>
            <p>{{ testimonial.quote }}</p>
            <div class="testimonial-person">
              <strong>{{ testimonial.name }}</strong>
              <span>{{ testimonial.role }}</span>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="faq-section" aria-label="Questions fréquentes">
      <div class="section-inner faq-inner">
        <div class="section-heading centered">
          <span class="section-eyebrow">Questions fréquentes</span>
          <h2>Les réponses utiles avant de créer votre compte</h2>
        </div>

        <div class="faq-list">
          <GlassCollapse v-for="faq in faqs" :key="faq.question" :title="faq.question">
            <p>{{ faq.answer }}</p>
          </GlassCollapse>
        </div>
      </div>
    </section>

    <section class="final-cta" aria-label="Inscription">
      <div class="final-cta-inner">
        <img src="@/assets/logo.png" alt="Vitrail-A" class="final-cta-logo" />
        <h2>Rejoignez plus de 250 propriétaires et éleveurs sur Vitrail-A</h2>
        <p>Créez votre compte pour gérer vos animaux, commander vos cartes QR et présenter un dossier santé clair aux professionnels qui vous accompagnent.</p>
        <div class="final-cta-buttons">
          <GlassButton variant="success" icon="fas fa-user-plus" @click="$router.push('/inscription')">
            Créer un compte
          </GlassButton>
          <GlassButton variant="primary" icon="fas fa-user-md" @click="$router.push('/demande-pro-sante')">
            Espace vétérinaire
          </GlassButton>
        </div>
        <div class="login-prompt">
          <span>Déjà inscrit ?</span>
          <router-link to="/connexion" class="login-link">
            Se connecter <i class="fas fa-sign-in-alt"></i>
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import GlassIcon from '@/components/ui/GlassIcon.vue';

export default {
  name: 'IntroSection',
  components: {
    GlassIcon
  },
  data() {
    return {
      heroPillars: [
        { icon: 'fas fa-notes-medical', title: 'Dossier médical centralisé' },
        { icon: 'fas fa-qrcode', title: 'Carte physique avec QR code' },
        { icon: 'fas fa-store', title: 'Marketplace intégrée' }
      ],
      heroBadges: [
        { icon: 'fas fa-shield-alt', label: 'Données chiffrées' },
        { icon: 'fas fa-user-check', label: 'Vétérinaires vérifiés' },
        { icon: 'fas fa-mobile-alt', label: 'Accessible mobile' },
        { icon: 'fas fa-map-marker-alt', label: 'Géolocalisation' }
      ],
      stats: [
        { icon: 'fas fa-paw', value: '250+', label: 'animaux enregistrés' },
        { icon: 'fas fa-user-md', value: '12', label: 'vétérinaires vérifiés' },
        { icon: 'fas fa-star', value: '98%', label: 'satisfaction client' },
        { icon: 'fas fa-balance-scale', value: 'RGPD', label: 'données protégées' }
      ],
      processSteps: [
        {
          number: '01',
          icon: 'fas fa-user-plus',
          title: 'Créez votre compte',
          text: 'Inscrivez-vous en quelques instants et ajoutez vos informations essentielles.'
        },
        {
          number: '02',
          icon: 'fas fa-horse',
          title: 'Ajoutez vos animaux',
          text: 'Renseignez l’identification, les informations santé et les documents importants.'
        },
        {
          number: '03',
          icon: 'fas fa-id-card',
          title: 'Recevez la carte QR',
          text: 'Commandez une carte physique reliée au dossier numérique de chaque animal.'
        },
        {
          number: '04',
          icon: 'fas fa-share-alt',
          title: 'Partagez le dossier',
          text: 'Votre vétérinaire ou futur acheteur accède aux informations utiles au bon moment.'
        }
      ],
      cardFeatures: [
        'Format carte bancaire, facile à garder avec les papiers de l’animal',
        'QR code relié au dossier santé et aux informations d’identification',
        'Livraison standard, express ou premium selon votre besoin',
        'Traçabilité renforcée pour les ventes, transferts et consultations'
      ],
      audienceSections: [
        {
          eyebrow: 'Propriétaires',
          title: 'Gardez le dossier de vos chevaux toujours disponible',
          tagline: 'Identité, santé, localisation et propriété réunies dans une interface claire.',
          ariaLabel: 'Avantages pour les propriétaires de chevaux',
          glassIcon: 'horse',
          iconVariant: 'gradient-success',
          iconClass: 'success',
          buttonVariant: 'success',
          ctaLabel: 'Créer mon compte propriétaire',
          ctaRoute: '/inscription',
          benefits: [
            {
              icon: 'fas fa-file-medical-alt',
              title: 'Historique santé lisible',
              text: 'Traitements, radiographies et observations restent regroupés dans un dossier facile à consulter.'
            },
            {
              icon: 'fas fa-exchange-alt',
              title: 'Transfert sécurisé',
              text: 'La propriété peut être transférée avec validation et notification automatique.'
            },
            {
              icon: 'fas fa-map-marked-alt',
              title: 'Localisation suivie',
              text: 'Ajoutez une adresse ou une position GPS pour garder une trace des déplacements importants.'
            },
            {
              icon: 'fas fa-shopping-cart',
              title: 'Vente plus rassurante',
              text: 'Présentez un dossier clair aux acheteurs grâce à la marketplace et au lien partageable.'
            }
          ]
        },
        {
          eyebrow: 'Éleveurs',
          title: 'Valorisez vos animaux avec une traçabilité professionnelle',
          tagline: 'Une fiche complète aide vos acheteurs à comprendre l’origine, la santé et le suivi de chaque animal.',
          ariaLabel: 'Avantages pour les éleveurs',
          glassIcon: 'user',
          iconVariant: 'gradient-success',
          iconClass: 'success',
          buttonVariant: 'success',
          ctaLabel: 'Créer mon compte éleveur',
          ctaRoute: '/inscription',
          alt: true,
          benefits: [
            {
              icon: 'fas fa-passport',
              title: 'Identification complète',
              text: 'UELN, SIRE, micropuce, race et livre généalogique sont structurés dès l’enregistrement.'
            },
            {
              icon: 'fas fa-sitemap',
              title: 'Filiation conservée',
              text: 'La lignée peut être suivie avec les informations du père et de la mère.'
            },
            {
              icon: 'fas fa-handshake',
              title: 'Marketplace intégrée',
              text: 'Publiez vos annonces et facilitez la vente avec paiement sécurisé et transfert de propriété.'
            },
            {
              icon: 'fas fa-coins',
              title: 'Cashback éleveur',
              text: 'Un cashback de 10 % peut accompagner les reventes réalisées sur la plateforme.'
            }
          ]
        },
        {
          eyebrow: 'Vétérinaires',
          title: 'Accédez au bon dossier au moment de la consultation',
          tagline: 'Un espace professionnel vérifié pour consulter l’historique et enrichir le suivi médical.',
          ariaLabel: 'Avantages pour les vétérinaires',
          glassIcon: 'stethoscope',
          iconVariant: 'gradient-primary',
          iconClass: 'medical',
          buttonVariant: 'primary',
          ctaLabel: 'Demander mon compte vétérinaire',
          ctaRoute: '/demande-pro-sante',
          benefits: [
            {
              icon: 'fas fa-user-md',
              title: 'Compte vérifié',
              text: 'Les demandes vétérinaires sont validées pour garder un accès professionnel maîtrisé.'
            },
            {
              icon: 'fas fa-qrcode',
              title: 'Scan QR rapide',
              text: 'La carte de l’animal permet de rejoindre son dossier sans perdre de temps.'
            },
            {
              icon: 'fas fa-stethoscope',
              title: 'Traitements et notes',
              text: 'Ajoutez les actes, observations et informations utiles au suivi clinique.'
            },
            {
              icon: 'fas fa-x-ray',
              title: 'Radiographies protégées',
              text: 'Les images médicales peuvent être déposées et conservées de façon sécurisée.'
            }
          ]
        },
        {
          eyebrow: 'Animaux de compagnie',
          title: 'Le même niveau de suivi pour chiens, chats et nouveaux compagnons',
          tagline: 'Une plateforme unique pour les animaux du foyer, pas seulement les chevaux.',
          ariaLabel: 'Avantages pour les propriétaires d’animaux de compagnie',
          glassIcon: 'paw',
          iconVariant: 'gradient-success',
          iconClass: 'success',
          buttonVariant: 'success',
          ctaLabel: 'Créer mon compte',
          ctaRoute: '/inscription',
          alt: true,
          benefits: [
            {
              icon: 'fas fa-paw',
              title: 'Multi-espèces',
              text: 'Chiens, chats, oiseaux, lapins ou reptiles peuvent rejoindre le même espace de gestion.'
            },
            {
              icon: 'fas fa-microchip',
              title: 'Identification pratique',
              text: 'Micropuce, tatouage, passeport et informations santé restent facilement accessibles.'
            },
            {
              icon: 'fas fa-syringe',
              title: 'Suivi vaccinal',
              text: 'Gardez une trace claire des vaccinations et des informations importantes.'
            },
            {
              icon: 'fas fa-id-card',
              title: 'Carte et lien partageable',
              text: 'Partagez les informations utiles avec un professionnel ou une personne de confiance.'
            }
          ]
        }
      ],
      testimonials: [
        {
          quote: 'À remplacer par un avis vérifié de propriétaire : dossier santé plus clair, carte QR utile lors des déplacements et meilleure visibilité en cas de vente.',
          name: 'Avis propriétaire',
          role: 'Chevaux et animaux de compagnie'
        },
        {
          quote: 'À remplacer par un avis vérifié d’éleveur : identification plus professionnelle, suivi de filiation lisible et vente plus rassurante pour les acheteurs.',
          name: 'Avis éleveur',
          role: 'Élevage équin'
        },
        {
          quote: 'À remplacer par un avis vérifié de vétérinaire : accès rapide aux informations utiles, historique médical centralisé et dépôt sécurisé des observations.',
          name: 'Avis vétérinaire',
          role: 'Professionnel de santé animale'
        }
      ],
      faqs: [
        {
          question: 'Combien coûte la carte physique Vitrail-A ?',
          answer: 'Le prix dépend du type de carte et du mode de livraison choisi. Le panier vous permet de sélectionner la livraison standard, express ou premium avant paiement.'
        },
        {
          question: 'Quels animaux peuvent être enregistrés ?',
          answer: 'La plateforme prend en charge les chevaux ainsi que plusieurs animaux de compagnie comme les chiens, chats, oiseaux, lapins et reptiles.'
        },
        {
          question: 'Mes données médicales sont-elles protégées ?',
          answer: 'Oui. Les informations sensibles et les fichiers médicaux sont protégés par des contrôles d’accès, du chiffrement et une gestion conforme aux attentes RGPD.'
        },
        {
          question: 'Un vétérinaire peut-il consulter le dossier de mon animal ?',
          answer: 'Les vétérinaires vérifiés peuvent accéder aux dossiers nécessaires à leur travail clinique. Vous pouvez aussi partager certaines informations grâce au lien ou au QR code.'
        },
        {
          question: 'Comment devenir vétérinaire sur Vitrail-A ?',
          answer: 'Un vétérinaire peut demander un compte professionnel, transmettre ses justificatifs, puis finaliser son inscription après validation administrative.'
        },
        {
          question: 'La marketplace est-elle réservée aux chevaux ?',
          answer: 'Non. Vitrail-A permet de publier des annonces pour les chevaux et pour les animaux de compagnie, avec un parcours sécurisé pour la mise en vente.'
        }
      ]
    };
  },
  methods: {
    scrollToSection(id) {
      const section = document.getElementById(id);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }
};
</script>

<style scoped>
.intro-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
}

.hero-section,
.stats-section,
.process-section,
.card-spotlight,
.benefit-section,
.testimonials-section,
.faq-section,
.final-cta {
  position: relative;
  z-index: 10;
}

.hero-section {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 82vh;
  padding: var(--space-16) var(--space-6) var(--space-10);
}

.hero-content {
  text-align: center;
  max-width: 860px;
  animation: fadeInUp var(--transition-slow);
}

.logo-container {
  display: flex;
  justify-content: center;
  margin-bottom: var(--space-5);
}

.hero-logo {
  width: 118px;
  height: 118px;
  object-fit: contain;
  border-radius: var(--radius-sm);
  filter: drop-shadow(var(--shadow-md));
}

.hero-kicker,
.section-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0 0 var(--space-3);
  color: var(--theme-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0;
  text-transform: uppercase;
}

.hero-title {
  color: var(--color-text);
  font-size: var(--font-size-4xl);
  font-weight: 800;
  letter-spacing: 0;
  line-height: var(--line-height-tight);
  margin: 0 0 var(--space-4);
  text-shadow: var(--shadow-sm);
}

.hero-subtitle {
  color: var(--color-text-light);
  font-size: var(--font-size-lg);
  line-height: var(--line-height-relaxed);
  margin: 0 auto var(--space-8);
  max-width: 760px;
}

.hero-pillars {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-3);
  margin-bottom: var(--space-6);
}

.hero-pillar,
.badge,
.stat-item,
.benefit-card,
.testimonial-card,
.final-cta-inner,
.health-card-mock {
  background: var(--glass-bg-solid);
  border: 1px solid var(--glass-border);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: var(--shadow-glass);
}

.hero-pillar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border-radius: var(--radius-sm);
  min-height: 64px;
  padding: var(--space-3) var(--space-4);
  color: var(--color-text);
  font-weight: var(--font-weight-semibold);
}

.hero-pillar i,
.badge i,
.stat-item > i,
.spotlight-list i {
  color: var(--theme-primary);
}

.hero-badges {
  display: flex;
  justify-content: center;
  gap: var(--space-3);
  flex-wrap: wrap;
  margin-bottom: var(--space-8);
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  min-height: 40px;
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-full);
  color: var(--color-text);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.hero-cta,
.final-cta-buttons {
  display: flex;
  justify-content: center;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.stats-section {
  padding: 0 var(--space-6) var(--space-8);
  margin-top: calc(-1 * var(--space-6));
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-4);
  max-width: 1120px;
  margin: 0 auto;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  border-radius: var(--radius-sm);
  padding: var(--space-5);
}

.stat-item > i {
  font-size: var(--font-size-2xl);
  flex-shrink: 0;
}

.stat-item div {
  display: flex;
  flex-direction: column;
}

.stat-item strong {
  color: var(--color-text);
  font-size: var(--font-size-2xl);
  font-weight: 800;
  line-height: var(--line-height-tight);
}

.stat-item span {
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
}

.section-inner,
.benefit-inner {
  max-width: 1120px;
  margin: 0 auto;
}

.process-section,
.card-spotlight,
.benefit-section,
.testimonials-section,
.faq-section,
.final-cta {
  padding: var(--space-16) var(--space-6);
}

.benefit-section.benefit-alt,
.testimonials-section {
  background: var(--glass-bg-light);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.section-heading {
  max-width: 740px;
  margin-bottom: var(--space-10);
}

.section-heading.centered {
  text-align: center;
  margin-left: auto;
  margin-right: auto;
}

.section-heading h2,
.spotlight-copy h2,
.section-header h2,
.final-cta-inner h2 {
  color: var(--color-text);
  font-weight: 800;
  letter-spacing: 0;
  line-height: var(--line-height-tight);
  margin: 0;
}

.section-heading h2,
.spotlight-copy h2 {
  font-size: var(--font-size-3xl);
}

.section-heading p,
.spotlight-copy p,
.section-tagline,
.final-cta-inner > p {
  color: var(--color-text-light);
  line-height: var(--line-height-relaxed);
}

.section-heading p {
  font-size: var(--font-size-base);
  margin: var(--space-4) 0 0;
}

.process-steps {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-4);
  position: relative;
}

.process-step {
  position: relative;
  min-height: 250px;
  padding: var(--space-6);
  border-radius: var(--radius-sm);
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  box-shadow: var(--shadow-md);
}

.step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-bottom: var(--space-5);
  border-radius: var(--radius-full);
  color: var(--color-text-inverse);
  background: linear-gradient(135deg, rgba(var(--theme-primary-rgb), 0.95), rgba(var(--theme-primary-rgb), 0.72));
  font-weight: var(--font-weight-bold);
}

.process-step > i {
  color: var(--theme-primary);
  font-size: var(--font-size-2xl);
  margin-bottom: var(--space-4);
}

.process-step h3,
.benefit-card h3 {
  color: var(--color-text);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-tight);
  margin: 0 0 var(--space-2);
}

.process-step p,
.benefit-card p,
.testimonial-card p,
.faq-list p {
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-relaxed);
  margin: 0;
}

.card-spotlight-inner {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(320px, 0.95fr);
  gap: var(--space-12);
  align-items: center;
}

.spotlight-copy p {
  margin: var(--space-4) 0 var(--space-6);
}

.spotlight-list {
  display: grid;
  gap: var(--space-3);
  list-style: none;
  margin: 0 0 var(--space-8);
  padding: 0;
}

.spotlight-list li {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  color: var(--color-text);
  line-height: var(--line-height-normal);
}

.spotlight-list i {
  margin-top: var(--space-1);
}

.card-preview {
  display: flex;
  justify-content: center;
  perspective: 900px;
}

.health-card-mock {
  width: min(100%, 420px);
  aspect-ratio: 1.58;
  border-radius: var(--radius-lg);
  padding: var(--space-6);
  transform: rotate(-3deg);
  position: relative;
  overflow: hidden;
}

.health-card-mock::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(var(--theme-primary-rgb), 0.22), rgba(var(--color-primary-rgb), 0.12));
  pointer-events: none;
}

.card-topline,
.card-animal,
.card-meta,
.qr-box {
  position: relative;
  z-index: 1;
}

.card-topline {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  color: var(--color-text);
  font-weight: 800;
}

.card-topline img {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-sm);
}

.card-animal {
  display: flex;
  flex-direction: column;
  margin-top: var(--space-10);
}

.card-animal strong {
  color: var(--color-text);
  font-size: var(--font-size-2xl);
  line-height: var(--line-height-tight);
}

.card-animal span,
.card-meta span {
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
}

.card-meta {
  display: flex;
  justify-content: space-between;
  margin-top: var(--space-6);
  padding-right: 98px;
}

.qr-box {
  position: absolute;
  right: var(--space-6);
  bottom: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 78px;
  height: 78px;
  border-radius: var(--radius-sm);
  background: var(--color-white);
  color: var(--color-text);
  font-size: var(--font-size-4xl);
  box-shadow: var(--shadow-sm);
}

.section-header {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  margin-bottom: var(--space-8);
}

.section-header h2 {
  font-size: var(--font-size-3xl);
  margin-bottom: var(--space-2);
}

.section-tagline {
  margin: 0;
}

.benefits-grid,
.testimonials-grid {
  display: grid;
  gap: var(--space-5);
}

.compact-grid {
  grid-template-columns: repeat(4, 1fr);
}

.benefit-card,
.testimonial-card {
  border-radius: var(--radius-sm);
  padding: var(--space-6);
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);
}

.benefit-card:hover,
.testimonial-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-xl);
}

.benefit-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-sm);
  margin-bottom: var(--space-4);
  color: var(--color-text-inverse);
  font-size: var(--font-size-lg);
}

.benefit-icon.success {
  background: linear-gradient(135deg, rgba(var(--color-success-rgb), 0.95), rgba(var(--color-success-rgb), 0.7));
}

.benefit-icon.medical {
  background: linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.95), rgba(var(--color-primary-rgb), 0.7));
}

.section-cta {
  text-align: center;
  padding-top: var(--space-8);
}

.testimonials-grid {
  grid-template-columns: repeat(3, 1fr);
}

.rating {
  display: flex;
  gap: var(--space-1);
  color: var(--color-warning);
  margin-bottom: var(--space-4);
}

.testimonial-card p {
  min-height: 126px;
}

.testimonial-person {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin-top: var(--space-5);
}

.testimonial-person strong {
  color: var(--color-text);
  font-weight: var(--font-weight-bold);
}

.testimonial-person span {
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
}

.faq-inner {
  max-width: 860px;
}

.faq-list {
  display: grid;
  gap: var(--space-3);
}

.faq-list :deep(.collapse-trigger) {
  background: var(--glass-bg-solid);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-sm);
  color: var(--color-text);
  padding: var(--space-4) var(--space-5);
  font-weight: var(--font-weight-semibold);
}

.faq-list :deep(.collapse-trigger.is-open) {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.faq-list :deep(.collapse-content) {
  background: var(--glass-bg);
  border-color: var(--glass-border);
  border-bottom-left-radius: var(--radius-sm);
  border-bottom-right-radius: var(--radius-sm);
}

.faq-list :deep(.collapse-inner) {
  padding: var(--space-4) var(--space-5);
}

.final-cta {
  text-align: center;
  padding-bottom: var(--space-12);
}

.final-cta-inner {
  max-width: 720px;
  margin: 0 auto;
  border-radius: var(--radius-sm);
  padding: var(--space-12) var(--space-8);
}

.final-cta-logo {
  width: 80px;
  height: 80px;
  object-fit: contain;
  border-radius: var(--radius-sm);
  filter: drop-shadow(var(--shadow-sm));
}

.final-cta-inner h2 {
  font-size: var(--font-size-3xl);
  margin: var(--space-5) 0 var(--space-4);
}

.final-cta-inner > p {
  margin: 0 0 var(--space-8);
}

.login-prompt {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  margin-top: var(--space-6);
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
}

.login-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--theme-primary);
  font-weight: var(--font-weight-semibold);
  text-decoration: none;
  transition: gap var(--transition-fast), color var(--transition-fast);
}

.login-link:hover {
  color: var(--theme-primary-dark);
  gap: var(--space-3);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1100px) {
  .compact-grid,
  .process-steps {
    grid-template-columns: repeat(2, 1fr);
  }

  .card-spotlight-inner,
  .testimonials-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .stats-container,
  .hero-pillars {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .hero-section {
    min-height: 76vh;
    padding: var(--space-12) var(--space-4) var(--space-8);
  }

  .hero-logo {
    width: 96px;
    height: 96px;
  }

  .hero-title {
    font-size: var(--font-size-3xl);
  }

  .hero-subtitle {
    font-size: var(--font-size-base);
  }

  .process-section,
  .card-spotlight,
  .benefit-section,
  .testimonials-section,
  .faq-section,
  .final-cta {
    padding: var(--space-12) var(--space-4);
  }

  .hero-pillars,
  .stats-container,
  .compact-grid,
  .process-steps {
    grid-template-columns: 1fr;
  }

  .section-header {
    align-items: flex-start;
  }

  .section-heading h2,
  .spotlight-copy h2,
  .section-header h2,
  .final-cta-inner h2 {
    font-size: var(--font-size-2xl);
  }

  .health-card-mock {
    transform: none;
  }

  .card-meta {
    flex-direction: column;
    gap: var(--space-2);
    padding-right: 92px;
  }

  .testimonial-card p {
    min-height: auto;
  }

  .final-cta-inner {
    padding: var(--space-8) var(--space-5);
  }
}

@media (max-width: 520px) {
  .hero-cta,
  .final-cta-buttons,
  .login-prompt {
    align-items: stretch;
    flex-direction: column;
  }

  .section-header {
    flex-direction: column;
  }

  .health-card-mock {
    padding: var(--space-5);
  }

  .card-animal {
    margin-top: var(--space-8);
  }

  .qr-box {
    right: var(--space-5);
    bottom: var(--space-5);
    width: 68px;
    height: 68px;
    font-size: var(--font-size-3xl);
  }
}
</style>